// Copyright (c) Ping. All rights reserved.
//
// The active-call surface for web. Scaffolding matches the linked Figma desktop
// frames (Calling 13:6718, Connected/PIP 13:6835, Group 13:6950); the control
// bar is the DS variant (@ping/ui CallControlBar, 889:11586). Integrates the
// Call Thread + Notes + People + Invite panels, the DS reaction + more +
// device + background-effects menus, flying reactions, the annotation overlay,
// the draggable minimized card, an E2EE/connection indicator, a live duration
// timer, active-speaker ordering, and an audio-unblock prompt.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, Text, View, type ViewStyle } from 'react-native';

import {
  Eraser,
  Hand,
  Lock,
  Maximize2,
  Minimize2,
  PictureInPicture2,
  ShieldAlert,
  Volume2,
  X,
} from 'lucide-react-native';
import { useShallow } from 'zustand/react/shallow';

import { Avatar, CallControlBar, hexToBytes, type ThreadIdentifier, useConversations } from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import type { LiveKitParticipantView } from '@calls/livekit/LiveKitCallClient';
import { useCallStore } from '@calls/store/callStore';
import { createWebThreadGateway } from '@features/chat/webThreadGateway';

import { AnchoredPopover } from './AnchoredPopover';
import { BackgroundEffectsMenu } from './BackgroundEffectsMenu';
import { CallEffectOverlay } from './CallEffectOverlay';
import { CallNotesPanel } from './CallNotesPanel';
import { useCallIconColors } from './callTheme';
import { CallThreadPanel } from './CallThreadPanel';
import { CallVideoTile } from './CallVideoTile';
import { DeviceMenu } from './DeviceMenu';
import { DrawingCanvas } from './DrawingCanvas';
import { GroupGrid } from './GroupGrid';
import { InviteMenu } from './InviteMenu';
import { MinimizedCallCard } from './MinimizedCallCard';
import { MoreMenu } from './MoreMenu';
import { ParticipantTile } from './ParticipantTile';
import { PeoplePanel } from './PeoplePanel';
import {
  ScreenShareConnecting,
  ScreenStage,
  SpotlightLayout,
} from './PresentationStage';
import { ReactionFly } from './ReactionFly';
import { ReactionMenu } from './ReactionMenu';
import { useCallDuration } from './useCallDuration';
import { useCallThreadWiring } from './useCallThreadWiring';
import { selectVideoIdentities } from './videoBudget';
import { VoiceMenu } from './VoiceMenu';

const DRAW_COLORS = ['#FF5353', '#3CCB4F', '#537EFF', '#FFCB45', '#FFFFFF'];

const FIXED_FULL = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2147483600,
} as unknown as ViewStyle;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Fullscreen API surface, including the WebKit-prefixed variants. Desktop
// Safari only shipped the unprefixed `Element.requestFullscreen` /
// `document.fullscreenElement` in 16.4; everything older (and assorted embedded
// WebKit shells) exposes only `webkit*`. Without these fallbacks the fullscreen
// button is a silent no-op and its icon never syncs on Safari.
interface FsDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}
interface FsElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

function fullscreenElement(doc: FsDocument): Element | null {
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/**
 * Browser-fullscreen toggle for the call surface. `isFullscreen` mirrors the
 * actual document state (so the Esc-to-exit path keeps the icon in sync) and
 * works on Safari via the `webkit*` fallbacks.
 */
function useFullscreen(): { isFullscreen: boolean; toggle: () => void } {
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== 'undefined' && fullscreenElement(document as FsDocument) !== null,
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setIsFullscreen(fullscreenElement(document as FsDocument) !== null);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);
  const toggle = useCallback(() => {
    if (typeof document === 'undefined') return;
    const doc = document as FsDocument;
    if (fullscreenElement(doc) !== null) {
      void Promise.resolve(
        doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.(),
      ).catch(() => undefined);
    } else {
      const el = document.documentElement as FsElement;
      void Promise.resolve(
        el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.(),
      ).catch(() => undefined);
    }
  }, []);
  return { isFullscreen, toggle };
}

/**
 * Persistent presence badges — raised hand + "be right back". They stay on the
 * sender's card while toggled and disappear when cleared. Positioned per layout:
 * bottom-right on group tiles, top-left on the 1-1 self-view PIP.
 */
function PresenceBadges({
  handRaised,
  away,
  corner,
}: {
  handRaised: boolean;
  away: boolean;
  corner: 'bottom-right' | 'top-left';
}): JSX.Element | null {
  if (!handRaised && !away) return null;
  const pos = corner === 'top-left' ? 'left-[8px] top-[8px]' : 'bottom-[8px] right-[8px]';
  return (
    <View className={`absolute ${pos} flex-row items-center gap-[6px]`}>
      {handRaised ? (
        <View className="h-[28px] w-[28px] items-center justify-center rounded-full border border-white/[0.06] bg-black/50">
          <Hand size={15} color="#FFCB45" />
        </View>
      ) : null}
      {away ? (
        <View className="h-[28px] flex-row items-center gap-[4px] rounded-full border border-white/[0.06] bg-black/50 px-[8px]">
          <Text className="text-[13px]">☕️</Text>
          <Text className="text-[12px] font-semibold text-[#E8EAED]">Away</Text>
        </View>
      ) : null}
    </View>
  );
}

function DrawToolbar(): JSX.Element {
  const color = useCallStore((s) => s.drawColor);
  return (
    <View
      style={{ position: 'absolute', top: 12, left: '50%', marginLeft: -120, zIndex: 6 } as unknown as ViewStyle}
      className="flex-row items-center gap-[8px] rounded-full border border-[#2F333B] bg-[#15171C] px-[10px] py-[6px]"
    >
      {DRAW_COLORS.map((c) => (
        <Pressable
          key={c}
          accessibilityLabel={`Color ${c}`}
          onPress={() => useCallStore.getState().setDrawColor(c)}
          className={`h-[18px] w-[18px] rounded-full ${color === c ? 'border-2 border-white' : ''}`}
          style={{ backgroundColor: c }}
        />
      ))}
      <View className="h-[16px] w-px bg-[#2F333B]" />
      <Pressable accessibilityLabel="Clear drawing" onPress={() => void useCallStore.getState().clearDrawings()}>
        <Eraser size={16} color="#E8EAED" />
      </Pressable>
      <Pressable accessibilityLabel="Stop annotating" onPress={() => useCallStore.getState().toggleDrawMode()}>
        <X size={16} color="#E8EAED" />
      </Pressable>
    </View>
  );
}

export function ActiveCallView(): JSX.Element | null {
  const s = useCallStore(
    useShallow((st) => ({
      phase: st.phase,
      active: st.active,
      participants: st.participants,
      connState: st.connState,
      e2eeActive: st.e2eeActive,
      audioBlocked: st.audioBlocked,
      activeSpeakers: st.activeSpeakers,
      micEnabled: st.micEnabled,
      cameraEnabled: st.cameraEnabled,
      screenSharing: st.screenSharing,
      handRaised: st.handRaised,
      raisedHands: st.raisedHands,
      brb: st.brb,
      awayParticipants: st.awayParticipants,
      effects: st.effects,
      minimized: st.minimized,
      panel: st.panel,
      threadUnread: st.threadUnread,
      reactions: st.reactions,
      drawMode: st.drawMode,
      drawings: st.drawings,
      isRecording: st.isRecording,
      recordingAvailable: st.recordingAvailable,
    })),
  );
  const callThreadRoot = useCallStore((st) => st.callThreadRoot);

  // Conversation-scoped thread gateway — the SAME one the main chat thread uses,
  // so the in-call thread panel is byte-for-byte the chat-room thread UI. Built
  // here (always mounted) so it also feeds the unread badge while the panel is
  // closed. Re-created only when the host conversation / title changes.
  const threadConvId = s.active?.conversationId ?? null;
  const threadPeerName = s.active?.title ?? 'Call';
  // Per-sender author resolution for the in-call GROUP thread — resolve each
  // reply's account id → roster display name + avatar (instead of the call
  // title fallback). Read through a REF so the gateway memo below stays stable
  // (adding the live conversation list to its deps would re-bootstrap the panel
  // on every list tick).
  const { conversations } = useConversations();
  const activeConv = conversations.find((c) => c.id === threadConvId);
  const threadRosterRef = useRef<{ conv: typeof activeConv }>({ conv: activeConv });
  threadRosterRef.current = { conv: activeConv };
  const threadGateway = useMemo(
    () =>
      createWebThreadGateway({
        conversationId: threadConvId,
        peerName: threadPeerName,
        // Resolve a non-self reply author → name + avatar. A peer's sender id
        // is a DEVICE HASH; roster maps are keyed by ACCOUNT id, so resolve the
        // hash first, then fall back to the 1:1 conversation picture (which IS
        // the peer's avatar). Without this thread reply avatars showed a bare
        // initial + "Member".
        resolveAuthor: (rawSenderId: string) => {
          const conv = threadRosterRef.current.conv;
          const accountId = conv?.memberDeviceAccounts?.[rawSenderId] ?? rawSenderId;
          let avatarUri =
            (accountId.length > 0 ? conv?.memberAvatarUris?.[accountId] : undefined) ??
            conv?.memberAvatarUris?.[rawSenderId];
          if ((avatarUri === undefined || avatarUri.length === 0) && conv?.kind === 'direct') {
            avatarUri = conv.avatarUri ?? conv.avatarMediaId;
          }
          const name =
            (accountId.length > 0 ? conv?.memberDisplayNames?.[accountId] : undefined) ??
            conv?.memberDisplayNames?.[rawSenderId] ??
            conv?.memberDeviceNames?.[rawSenderId];
          const fallbackName = conv?.kind === 'direct' ? threadPeerName : 'Member';
          return {
            name: name !== undefined && name.length > 0 ? name : fallbackName,
            ...(avatarUri !== undefined && avatarUri.length > 0 ? { avatarUri } : {}),
          };
        },
        resolveConv: async () => {
          const status = useSessionStore.getState().status;
          const client =
            status.kind === 'authenticated'
              ? (status.client as unknown as {
                  getConversation(id: Uint8Array): { send(p: Uint8Array): Promise<unknown> };
                } | null)
              : null;
          if (client === null || threadConvId === null) return null;
          try {
            return client.getConversation(hexToBytes(threadConvId));
          } catch {
            return null;
          }
        },
      }),
    [threadConvId, threadPeerName],
  );

  // Unread badge: bump `threadUnread` when a new reply lands while the thread
  // panel is closed (the panel itself clears it via setPanel('thread')).
  const lastReplyCountRef = useRef<number | null>(null);
  useEffect(() => {
    lastReplyCountRef.current = null;
    if (callThreadRoot === null) return;
    const unsub = threadGateway.subscribe(callThreadRoot as ThreadIdentifier, (snap) => {
      const n = snap.replies.length;
      const prev = lastReplyCountRef.current;
      lastReplyCountRef.current = n;
      if (prev === null) return; // first snapshot only sets the baseline
      if (n > prev && useCallStore.getState().panel !== 'thread') {
        useCallStore.setState({ threadUnread: useCallStore.getState().threadUnread + (n - prev) });
      }
    });
    return unsub;
  }, [callThreadRoot, threadGateway]);

  // In-call thread parity — the SAME rowActions (reactions, message actions,
  // retry, read receipts, media variants) + rich composer (mentions /
  // attachments / voice / GIF) the main chat thread uses.
  const sessionClient = useSessionStore((st) =>
    st.status.kind === 'authenticated' ? st.status.client : null,
  );
  const callThreadRootRef = useRef<string | null>(null);
  callThreadRootRef.current = callThreadRoot;
  const callThreadForwardTargets = useMemo(
    () =>
      conversations
        .filter((c) => c.id !== threadConvId)
        .map((c) => ({ id: c.id, name: c.name ?? 'Chat' })),
    [conversations, threadConvId],
  );
  const callThreadWiring = useCallThreadWiring({
    conversationId: threadConvId,
    client: sessionClient,
    ...(activeConv?.name !== undefined ? { conversationName: activeConv.name } : {}),
    gateway: threadGateway,
    openThreadId: callThreadRoot,
    threadRootRef: callThreadRootRef,
    roster: {
      memberDisplayNames: activeConv?.memberDisplayNames,
      memberDeviceNames: activeConv?.memberDeviceNames,
      memberDeviceAccounts: activeConv?.memberDeviceAccounts,
      memberAvatarUris: activeConv?.memberAvatarUris,
      memberAccountIds: activeConv?.memberAccountIds,
      isGroup: activeConv?.kind === 'group',
    },
    forwardTargets: callThreadForwardTargets,
  });

  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Locally-pinned participant (per-viewer view preference) → spotlight layout.
  const [pinnedIdentity, setPinnedIdentity] = useState<string | null>(null);
  const [effectsOpen, setEffectsOpen] = useState(false);
  const [deviceMenu, setDeviceMenu] = useState<null | 'audioinput' | 'videoinput'>(null);
  // Trigger refs for anchored popovers. Typed loosely: at runtime these resolve
  // to the host DOM node (react-native-web), and the workspace has duplicate RN
  // `View` type versions across packages so a precise type can't bridge them.
   
  const reactionsRef = useRef<any>(null);
   
  const moreRef = useRef<any>(null);
   
  const micRef = useRef<any>(null);
   
  const camRef = useRef<any>(null);
  const duration = useCallDuration(s.active?.startedAt ?? null);
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
  const icon = useCallIconColors();
  // Stable across renders so the memoised ParticipantTiles aren't invalidated
  // every second by the duration tick / active-speaker churn.
  const togglePin = useCallback(
    (identity: string) => setPinnedIdentity((cur) => (cur === identity ? null : identity)),
    [],
  );

  const remotes = useMemo(() => {
    const speaking = new Set(s.activeSpeakers);
    return s.participants
      .filter((p) => !p.isLocal)
      .sort((a, b) => Number(speaking.has(b.identity)) - Number(speaking.has(a.identity)));
  }, [s.participants, s.activeSpeakers]);
  const local = useMemo(() => s.participants.find((p) => p.isLocal), [s.participants]);

  if (s.phase === 'idle' || s.active === null) return null;
  if (s.minimized) return <MinimizedCallCard />;

  const reconnecting = s.phase === 'reconnecting' || s.connState === 'reconnecting';
  const isConnecting = s.phase === 'connecting' || s.phase === 'outgoing';
  const speakingSet = new Set(s.activeSpeakers);

  // Group when the call is flagged group OR there are ≥2 remotes (covers any
  // path where the flag is missing, e.g. handoff).
  const isGroupCall = s.active.isGroup || remotes.length >= 2;
  // Stable order for the grid/filmstrip (local first, then by identity) so an
  // active-speaker change never reshuffles the cards.
  const everyone = [
    ...(local ? [local] : []),
    ...remotes.slice().sort((a, b) => a.identity.localeCompare(b.identity)),
  ] as LiveKitParticipantView[];

  // Decode budget — the key to staying smooth at scale, especially on Safari.
  // Only this bounded set of identities attaches a live <video> (= a WebRTC
  // decode); everyone else renders as an avatar tile and stays in the call as
  // audio + presence. Active speakers / pin / screen-share are prioritised, so
  // the people who matter are always the ones decoded. See videoBudget.ts.
  const videoIdentities = selectVideoIdentities(everyone, speakingSet, pinnedIdentity);

  // Screen-share takes priority for the stage. Prefer a remote presenter so a
  // local-only share still shows your own screen.
  const presenter =
    remotes.find((p) => p.screenTrack !== undefined) ??
    (local?.screenTrack !== undefined ? local : undefined);

  // A pin only switches layout when the pinned participant is still present.
  const pinned = pinnedIdentity !== null ? everyone.find((p) => p.identity === pinnedIdentity) : undefined;

  const closeAllMenus = () => {
    setReactionsOpen(false);
    setMoreOpen(false);
    setDeviceMenu(null);
  };

  return (
    <View style={FIXED_FULL} className="flex-row bg-[#ECEEF1] dark:bg-[#111111]">
      <View className="flex-1">
        {/* Top bar — Figma 4084:18439: only two bare icon buttons (minimize +
            maximize) flanking the centered call info (timer · E2E · lock). */}
        <View className="h-[40px] flex-row items-center justify-between px-[24px] py-[8px]">
          <Pressable
            onPress={() => useCallStore.getState().setMinimized(true)}
            accessibilityLabel="Minimize call"
            hitSlop={10}
            className="h-[24px] w-[24px] items-center justify-center active:opacity-60"
          >
            <PictureInPicture2 size={20} color={icon.muted} />
          </Pressable>

          <View className="flex-row items-center gap-[8px]">
            {s.active.isGroup ? (
              <Text className="text-sm font-bold text-[#15171C] dark:text-[#F5F6F8]">{s.active.title}</Text>
            ) : null}
            <Text className="text-sm text-[#15171C] dark:text-[#F5F6F8]">{duration}</Text>
            <View className="flex-row items-center gap-[4px]">
              <Text className="text-sm text-[#6B7280] dark:text-[#B5BAC3]">
                {s.e2eeActive ? 'End-to-end Encrypted' : 'Encrypted in transit'}
              </Text>
              {s.e2eeActive ? (
                <Lock size={12} color={icon.muted} />
              ) : (
                <ShieldAlert size={12} color="#FFCB45" />
              )}
            </View>
            {s.isRecording ? (
              <View className="flex-row items-center gap-[5px] rounded-full bg-[#FF5353]/20 px-[8px] py-[1px]">
                <View className="h-[7px] w-[7px] rounded-full bg-[#FF5353]" />
                <Text className="text-[12px] font-bold text-[#FF5353]">Recording</Text>
              </View>
            ) : null}
          </View>

          <Pressable
            accessibilityLabel={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onPress={toggleFullscreen}
            hitSlop={10}
            className="h-[24px] w-[24px] items-center justify-center active:opacity-60"
          >
            {isFullscreen ? (
              <Minimize2 size={20} color={icon.muted} />
            ) : (
              <Maximize2 size={20} color={icon.muted} />
            )}
          </Pressable>
        </View>

        {/* Reconnecting banner — shown for ALL layouts (group + 1-1 + spotlight)
            while LiveKit re-establishes the connection after a network blip. */}
        {reconnecting ? (
          <View className="mx-auto mb-[4px] flex-row items-center gap-[8px] rounded-full bg-[#FFCB45]/20 px-[14px] py-[6px]">
            <View className="h-[7px] w-[7px] rounded-full bg-[#FFCB45]" />
            <Text className="text-[13px] font-bold text-[#FFCB45]">Reconnecting…</Text>
          </View>
        ) : null}

        {/* Audio-unblock prompt (Safari/Chrome autoplay) */}
        {s.audioBlocked ? (
          <Pressable
            onPress={() => void useCallStore.getState().resumeAudio()}
            className="mx-auto mb-[4px] flex-row items-center gap-[8px] rounded-full bg-[#537EFF] px-[14px] py-[6px] active:opacity-80"
          >
            <Volume2 size={16} color="#FFFFFF" />
            <Text className="text-[13px] font-bold text-white">Tap to enable call sound</Text>
          </Pressable>
        ) : null}

        {/* Main area (annotation overlays it) */}
        <View className="relative flex-1 px-[20px] pb-[12px]">
          {presenter !== undefined ? (
            // Screen-share: shared surface on the stage, everyone in the right
            // filmstrip (Figma 2444:6995). A participant can still be pinned
            // during the share — they surface as a prominent tile over the
            // screen (and drop out of the filmstrip to avoid a duplicate).
            <SpotlightLayout
              stage={
                <ScreenStage
                  presenter={presenter}
                  {...(pinned !== undefined && pinned.identity !== presenter.identity
                    ? {
                        pinnedOverlay: (
                          <ParticipantTile
                            p={pinned}
                            speaking={speakingSet.has(pinned.identity)}
                            handRaised={pinned.isLocal ? s.handRaised : !!s.raisedHands[pinned.identity]}
                            away={pinned.isLocal ? s.brb : !!s.awayParticipants[pinned.identity]}
                            pinned
                            onTogglePin={togglePin}
                          />
                        ),
                      }
                    : {})}
                />
              }
              filmstrip={
                pinned !== undefined && pinned.identity !== presenter.identity
                  ? everyone.filter((p) => p.identity !== pinned.identity)
                  : everyone
              }
              speakingSet={speakingSet}
              pinnedIdentity={pinnedIdentity}
              onTogglePin={togglePin}
              selfHand={s.handRaised}
              selfAway={s.brb}
              raisedHands={s.raisedHands}
              awayParticipants={s.awayParticipants}
              videoIdentities={videoIdentities}
            />
          ) : s.screenSharing ? (
            // Local share toggled on but the track hasn't published yet.
            <ScreenShareConnecting name="You" />
          ) : pinned !== undefined ? (
            // Pinned participant → spotlight stage + others on the right
            // (mirrors the share-screen layout).
            <SpotlightLayout
              stage={
                <ParticipantTile
                  p={pinned}
                  speaking={speakingSet.has(pinned.identity)}
                  handRaised={pinned.isLocal ? s.handRaised : !!s.raisedHands[pinned.identity]}
                  away={pinned.isLocal ? s.brb : !!s.awayParticipants[pinned.identity]}
                  pinned
                  onTogglePin={togglePin}
                />
              }
              filmstrip={everyone.filter((p) => p.identity !== pinned.identity)}
              speakingSet={speakingSet}
              pinnedIdentity={pinnedIdentity}
              onTogglePin={togglePin}
              selfHand={s.handRaised}
              selfAway={s.brb}
              raisedHands={s.raisedHands}
              awayParticipants={s.awayParticipants}
              videoIdentities={videoIdentities}
            />
          ) : isGroupCall ? (
            // Figma "Participants Container" (2444:8051): panel + 24 padding.
            <View className="flex-1 rounded-[12px] border border-[#E1E3E8] bg-[#F7F8FA] p-[24px] dark:border-[#222429] dark:bg-[#15171C]">
              <GroupGrid
                participants={everyone}
                speakingSet={speakingSet}
                pinnedIdentity={pinnedIdentity}
                onTogglePin={togglePin}
                selfHand={s.handRaised}
                selfAway={s.brb}
                raisedHands={s.raisedHands}
                awayParticipants={s.awayParticipants}
                videoIdentities={videoIdentities}
              />
            </View>
          ) : (
            <View className="relative flex-1 items-center justify-center overflow-hidden rounded-[15px]">
              {remotes[0]?.videoTrack ? (
                <CallVideoTile track={remotes[0]!.videoTrack} />
              ) : (
                <View className="flex-1 items-center justify-center gap-[10px]">
                  <Avatar
                    size="large"
                    initials={initials(s.active.title)}
                    {...(s.active.avatarUri !== undefined ? { imageUri: s.active.avatarUri } : {})}
                  />
                  <Text className="text-[20px] font-bold text-[#15171C] dark:text-white">{s.active.title}</Text>
                  <Text className="text-[16px] text-[#6B7280] dark:text-[#A6A6A6]">
                    {reconnecting
                      ? 'Reconnecting…'
                      : isConnecting || remotes.length === 0
                        ? 'Calling…'
                        : duration}
                  </Text>
                </View>
              )}

              {/* Remote peer's hand/away — top-left of the main 1-1 view. */}
              {remotes[0] !== undefined ? (
                <PresenceBadges
                  handRaised={!!s.raisedHands[remotes[0].identity]}
                  away={!!s.awayParticipants[remotes[0].identity]}
                  corner="top-left"
                />
              ) : null}

              {local?.videoTrack ? (
                <View className="absolute bottom-[16px] right-[16px] h-[120px] w-[220px] overflow-hidden rounded-[12px] border border-white/[0.08]">
                  <CallVideoTile track={local.videoTrack} mirror />
                  {/* Self hand/away — top-left of the PIP (1-1 mode). */}
                  <PresenceBadges handRaised={s.handRaised} away={s.brb} corner="top-left" />
                </View>
              ) : null}
            </View>
          )}

          {/* Annotation overlay (renders strokes always; captures in draw mode) */}
          <DrawingCanvas drawMode={s.drawMode} strokes={s.drawings} />
          {s.drawMode ? <DrawToolbar /> : null}

          <ReactionFly reactions={s.reactions} />
          <CallEffectOverlay effects={s.effects} />
        </View>

        {/* Popovers — anchored to their trigger button, flip + clamp smartly */}
        <AnchoredPopover
          anchorRef={reactionsRef}
          open={reactionsOpen}
          onClose={() => setReactionsOpen(false)}
        >
          <ReactionMenu
            handRaised={s.handRaised}
            brb={s.brb}
            onReact={(emoji) => void useCallStore.getState().sendReaction(emoji)}
            onToggleHand={() => void useCallStore.getState().toggleHandRaise()}
            onToggleBrb={() => void useCallStore.getState().toggleBrb()}
            onEffect={(effect) => void useCallStore.getState().sendEffect(effect)}
            onClose={() => setReactionsOpen(false)}
          />
        </AnchoredPopover>
        <AnchoredPopover anchorRef={moreRef} open={moreOpen} onClose={() => setMoreOpen(false)}>
          <MoreMenu
            drawMode={s.drawMode}
            isRecording={s.isRecording}
            recordingAvailable={s.recordingAvailable}
            onBackgroundEffects={() => {
              setMoreOpen(false);
              setEffectsOpen(true);
            }}
            onToggleDraw={() => {
              setMoreOpen(false);
              useCallStore.getState().toggleDrawMode();
            }}
            onToggleRecording={() => {
              setMoreOpen(false);
              void useCallStore.getState().toggleRecording();
            }}
            onHandoff={() => {
              setMoreOpen(false);
              void useCallStore.getState().handoffCall();
            }}
            onViewParticipants={() => {
              setMoreOpen(false);
              useCallStore.getState().setPanel('people');
            }}
          />
        </AnchoredPopover>
        {/* Mic caret → full Voice menu (input/output device + volume); camera
            caret → a simple camera device list. */}
        <AnchoredPopover
          anchorRef={deviceMenu === 'videoinput' ? camRef : micRef}
          open={deviceMenu !== null}
          onClose={() => setDeviceMenu(null)}
          placement="top"
        >
          {deviceMenu === 'videoinput' ? (
            <DeviceMenu kind="videoinput" title="Camera" onClose={() => setDeviceMenu(null)} />
          ) : (
            <VoiceMenu onClose={() => setDeviceMenu(null)} />
          )}
        </AnchoredPopover>

        {/* Control bar — full-width SPACE_BETWEEN bar (Figma 889:11587). */}
        <View className="pb-[12px] pt-[8px]">
          <CallControlBar
            reactionsRef={reactionsRef}
            moreRef={moreRef}
            micMenuRef={micRef}
            camMenuRef={camRef}
            micEnabled={s.micEnabled}
            cameraEnabled={s.cameraEnabled}
            screenSharing={s.screenSharing}
            reactionsActive={reactionsOpen}
            chatActive={s.panel === 'thread'}
            chatUnread={s.threadUnread}
            notesActive={s.panel === 'notes'}
            onToggleMic={() => void useCallStore.getState().toggleMic()}
            onToggleCamera={() => void useCallStore.getState().toggleCamera()}
            onMicMenu={() => setDeviceMenu(deviceMenu === 'audioinput' ? null : 'audioinput')}
            onCamMenu={() => setDeviceMenu(deviceMenu === 'videoinput' ? null : 'videoinput')}
            onToggleScreenShare={() => void useCallStore.getState().toggleScreenShare()}
            onReactions={() => {
              closeAllMenus();
              setReactionsOpen((v) => !v);
            }}
            onOpenChat={() =>
              useCallStore.getState().setPanel(s.panel === 'thread' ? 'none' : 'thread')
            }
            onOpenNotes={() =>
              useCallStore.getState().setPanel(s.panel === 'notes' ? 'none' : 'notes')
            }
            onAddParticipant={() => useCallStore.getState().setPanel('invite')}
            onMore={() => {
              closeAllMenus();
              setMoreOpen((v) => !v);
            }}
            onLeave={() => void useCallStore.getState().endCall()}
          />
        </View>
      </View>

      {/* Right panel */}
      {s.panel !== 'none' ? (
        <View className="py-[12px] pr-[12px]">
          {s.panel === 'thread' ? (
            <CallThreadPanel
              gateway={threadGateway}
              rowActions={callThreadWiring.rowActions}
              composerProps={callThreadWiring.composerProps}
              stagedAttachments={callThreadWiring.stagedAttachments}
              onRemoveStagedAttachment={callThreadWiring.onRemoveStagedAttachment}
              onFlushStagedAttachments={callThreadWiring.onFlushStagedAttachments}
            />
          ) : null}
          {s.panel === 'notes' ? <CallNotesPanel /> : null}
          {s.panel === 'invite' ? <InviteMenu onClose={() => useCallStore.getState().setPanel('none')} /> : null}
          {s.panel === 'people' ? (
            <PeoplePanel
              participants={s.participants}
              speakingSet={speakingSet}
              pinnedIdentity={pinnedIdentity}
              onTogglePin={togglePin}
              onClose={() => useCallStore.getState().setPanel('none')}
              onOpenThread={() => useCallStore.getState().setPanel('thread')}
            />
          ) : null}
        </View>
      ) : null}

      {/* Background effects — anchored popover off the "More" button. */}
      <AnchoredPopover anchorRef={moreRef} open={effectsOpen} onClose={() => setEffectsOpen(false)}>
        <BackgroundEffectsMenu onClose={() => setEffectsOpen(false)} />
      </AnchoredPopover>

      {/* Forward-message picker for an in-call thread reply's Forward action. */}
      {callThreadWiring.forwardOverlay}
      {callThreadWiring.overlayHost}
    </View>
  );
}
